import { KnowledgeBaseLink } from '@ardoq/knowledge-base';
import {
  DisabledFloatingActionBarAction,
  ExternalDropZonesProps,
  FloatingActionBarAction,
  Row,
} from '@ardoq/table';
import {
  APIAttachment,
  APIDocumentArchiveFolder,
  ArdoqId,
  NamedEntity,
} from '@ardoq/api-types';
import { t, type I18nLocale } from '@ardoq/i18n';
import { Locale } from '@ardoq/locale';
import { dispatchAction } from '@ardoq/rxbeach';
import {
  toExternalLinkProps,
  toLinkProps,
} from 'router/declarative/toLinkProps';
import { DocumentArchiveState } from '../../../documentArchive/DocumentArchivePage';
import {
  ContextMenuItem,
  DocumentArchiveFloatingActionBarProps,
  GroupedEntity,
} from '../../../documentArchive/DocumentArchivePageView';
import {
  DocumentArchiveCommands,
  getGlobalContextMenuItems,
  toBulkActionItems,
  toDocumentArchivePageViewProps,
} from '../../../documentArchive/toDocumentArchivePageViewProps';
import {
  dragCompleted,
  dragEnteredDropZone,
  dragExitedDropZone,
  dragInitiated,
} from '../../actions';
import {
  MOVE_TO_GLOBAL_DROP_ZONE_SELECTOR,
  WorkspaceDocumentArchivePageViewProps,
} from './WorkspaceDocumentArchivePageView';
import { DragState } from '../../streams/dragState$';
import { isTruthy } from '@ardoq/common-helpers';
import { scrollAndHighlightRow } from './actions';

export type WorkspaceDocumentArchiveCommands = {
  globalDocumentArchiveCommands: DocumentArchiveCommands;
  transformToGlobal: (
    workspaceId: ArdoqId,
    attachmentIds: ArdoqId[],
    folderIds: ArdoqId[]
  ) => void;
  toOrganizationArchive: VoidFunction;
};

export const toWorkspaceDocumentArchivePageViewProps = (
  state: DocumentArchiveState,
  folders: APIDocumentArchiveFolder[],
  workspaceAttachments: APIAttachment[],
  globalAttachments: APIAttachment[],
  workspaceId: ArdoqId,
  locale: Locale,
  commands: WorkspaceDocumentArchiveCommands,
  dragState: DragState,
  i18nLocale: I18nLocale
): WorkspaceDocumentArchivePageViewProps => {
  const baseProps = toDocumentArchivePageViewProps(
    state,
    folders,
    workspaceAttachments,
    workspaceId,
    locale,
    commands.globalDocumentArchiveCommands,
    i18nLocale
  );

  const selectedAttachmentIds = state.selectedIds.filter(id =>
    workspaceAttachments.some(att => att._id === id)
  );

  const selectedNamingConflict = selectedRowsNamingConflicts(
    baseProps.table.rows
      .filter(row => selectedAttachmentIds.includes(row.id))
      .map(row => row.data),
    globalAttachments
  );

  const selectedFolderIds = state.selectedIds.filter(id =>
    folders.some(folder => folder._id === id)
  );

  const getContextMenuItems = (
    rowData: GroupedEntity,
    selectedRows: GroupedEntity[]
  ): ContextMenuItem[] => {
    const globalContextMenuItems = getGlobalContextMenuItems(
      workspaceAttachments,
      commands.globalDocumentArchiveCommands.documentArchiveTable,
      i18nLocale
    )(rowData, selectedRows);

    const workspaceContextMenuItems = toWorkspaceContextMenuItems(
      workspaceId,
      commands,
      rowData,
      selectedRows,
      globalAttachments,
      i18nLocale
    );

    const deleteContextMenuItem = globalContextMenuItems.find(
      item => item.color === 'textError'
    );

    const globalContextMenuItemsWithoutDelete = globalContextMenuItems.filter(
      item => item.color !== 'textError'
    );

    return [
      ...globalContextMenuItemsWithoutDelete,
      ...workspaceContextMenuItems,
      ...[deleteContextMenuItem].filter(isTruthy),
    ];
  };

  // Override rows to include workspace-specific context menu items
  const rowsWithContextMenu: Row<GroupedEntity>[] = baseProps.table.rows.map(
    row => ({
      ...row,
      getContextMenuItems,
    })
  );

  const hoveringDropZone = dragState.hoveredDropZoneIds.includes(
    MOVE_TO_GLOBAL_DROP_ZONE_SELECTOR
  );

  const baseFloatingActionBar = baseProps.table.floatingActionBar;
  const floatingActionBar: DocumentArchiveFloatingActionBarProps | null =
    baseFloatingActionBar
      ? {
          ...baseFloatingActionBar,
          actions: toWorkspaceFloatingActionBarActions(
            workspaceId,
            selectedFolderIds,
            selectedAttachmentIds,
            selectedNamingConflict,
            commands,
            i18nLocale
          ),
        }
      : null;

  return {
    ...baseProps,
    i18nLocale,
    title: t(
      i18nLocale,
      'workspaceManager:workspaceDocuments',
      'Workspace documents'
    ),
    table: {
      ...baseProps.table,
      rows: rowsWithContextMenu,
      floatingActionBar,
      tablePrimaryActionButton: {
        label: t(
          i18nLocale,
          'workspaceManager:moveToOrganizationDocuments',
          'Move to organization documents'
        ),
        onClick: () => {
          commands.transformToGlobal(
            workspaceId,
            selectedAttachmentIds,
            selectedFolderIds
          );
        },
        disabled:
          state.selectedIds.length === 0 || selectedNamingConflict.length > 0,
        popover: toNamingConflictPopover(selectedNamingConflict, i18nLocale),
      },
      dragAndDroppable: {
        ...baseProps.table.dragAndDroppable,
        externalDrop: toExternalDropZoneProps(workspaceId, commands),
      },
    },
    dropZone: baseProps.dropZone.hovered
      ? baseProps.dropZone
      : {
          ...baseProps.dropZone,
          hovered: hoveringDropZone,
          dragActive: dragState.isDragging,
          text: hoveringDropZone
            ? t(
                i18nLocale,
                'workspaceManager:moveToOrganizationDocuments',
                'Move to organization documents'
              )
            : dragState.isDragging
              ? t(
                  i18nLocale,
                  'workspaceManager:dragDropToMoveToOrg',
                  'Drag and drop files here to move to organization documents'
                )
              : baseProps.dropZone.text,
        },
    toOrganizationArchive: toLinkProps({
      route: '/manage-organization/document-archive',
      params: {},
    }),
    helpArticleLink: toExternalLinkProps(KnowledgeBaseLink.DOCUMENT_ARCHIVE),
    showEmptyState: workspaceAttachments.length === 0 && folders.length === 0,
  };
};

const selectedRowsNamingConflicts = (
  selectedRows: GroupedEntity[],
  globalAttachments: APIAttachment[]
): NamedEntity[] =>
  selectedRows
    .filter(row => row._group.isFolder === false)
    .filter(attachment =>
      globalAttachments.some(
        globalAtt => globalAtt.filename === attachment._group.name
      )
    )
    .map(row => ({ _id: row.id, name: row._group.name }));

type RowSelection = {
  folders: GroupedEntity[];
  attachments: GroupedEntity[];
};

const toFoldersAndAttachments = (
  rowData: GroupedEntity,
  allSelectedRows: GroupedEntity[]
): RowSelection => {
  const isCurrentRowSelected = allSelectedRows.some(
    row => row.id === rowData.id
  );

  if (isCurrentRowSelected) {
    return {
      folders: allSelectedRows.filter(row => row._group.isFolder),
      attachments: allSelectedRows.filter(row => !row._group.isFolder),
    };
  }

  return {
    folders: rowData._group.isFolder
      ? [...allSelectedRows.filter(row => row._group.isFolder), rowData]
      : allSelectedRows.filter(row => row._group.isFolder),
    attachments: !rowData._group.isFolder
      ? [...allSelectedRows.filter(row => !row._group.isFolder), rowData]
      : allSelectedRows.filter(row => !row._group.isFolder),
  };
};

const toNamingConflictTooltip = (
  conflictCount: number,
  conflictingRowNames: string,
  i18nLocale: I18nLocale
): string =>
  conflictCount === 1
    ? t(
        i18nLocale,
        'workspaceManager:documentArchive.namingConflictTooltipSingular',
        { count: conflictCount, names: conflictingRowNames },
        '{{count}} selected row has the same name as a row in the organization documents. Please rename or deselect the following rows to enable moving them to the organization documents: {{names}}'
      )
    : t(
        i18nLocale,
        'workspaceManager:documentArchive.namingConflictTooltipPlural',
        { count: conflictCount, names: conflictingRowNames },
        '{{count}} selected rows have the same name as a row in the organization documents. Please rename or deselect the following rows to enable moving them to the organization documents: {{names}}'
      );

const toWorkspaceContextMenuItems = (
  workspaceId: ArdoqId,
  commands: WorkspaceDocumentArchiveCommands,
  rowData: GroupedEntity,
  selectedRows: GroupedEntity[],
  globalAttachments: APIAttachment[],
  i18nLocale: I18nLocale
): ContextMenuItem[] => {
  const { folders, attachments } = toFoldersAndAttachments(
    rowData,
    selectedRows
  );
  const totalSelected = folders.length + attachments.length;
  const baseLabel = t(
    i18nLocale,
    'workspaceManager:moveToOrganizationDocuments',
    'Move to organization documents'
  );
  const label =
    totalSelected > 1 ? `${baseLabel} (${totalSelected})` : baseLabel;

  const conflictingRows = selectedRowsNamingConflicts(
    attachments,
    globalAttachments
  );

  const conflictCount = conflictingRows.length;
  const conflictingRowNames = conflictingRows.map(row => row.name).join(', ');

  return [
    {
      name: label,
      label,
      disabled: conflictCount > 0,
      tooltip:
        conflictCount > 0
          ? toNamingConflictTooltip(
              conflictCount,
              conflictingRowNames,
              i18nLocale
            )
          : undefined,
      onClick: () => {
        commands.transformToGlobal(
          workspaceId,
          attachments.map(row => row.id),
          folders.map(row => row.id)
        );
      },
    },
  ];
};

const toMoveToOrgFloatingAction = (
  workspaceId: ArdoqId,
  selectedFolderIds: ArdoqId[],
  selectedAttachmentIds: ArdoqId[],
  namingConflicts: NamedEntity[],
  commands: WorkspaceDocumentArchiveCommands,
  i18nLocale: I18nLocale
): FloatingActionBarAction | DisabledFloatingActionBarAction => {
  const totalSelected = selectedFolderIds.length + selectedAttachmentIds.length;
  const baseLabel = t(
    i18nLocale,
    'workspaceManager:moveToOrganizationDocuments',
    'Move to organization documents'
  );
  const label =
    totalSelected > 1 ? `${baseLabel} (${totalSelected})` : baseLabel;
  const conflictCount = namingConflicts.length;

  if (conflictCount > 0) {
    const conflictingRowNames = namingConflicts.map(row => row.name).join(', ');
    return {
      isDisabled: true,
      tooltip: toNamingConflictTooltip(
        conflictCount,
        conflictingRowNames,
        i18nLocale
      ),
      label,
    };
  }

  return {
    label,
    onClick: () =>
      commands.transformToGlobal(
        workspaceId,
        selectedAttachmentIds,
        selectedFolderIds
      ),
  };
};

const toWorkspaceFloatingActionBarActions = (
  workspaceId: ArdoqId,
  selectedFolderIds: ArdoqId[],
  selectedAttachmentIds: ArdoqId[],
  namingConflicts: NamedEntity[],
  commands: WorkspaceDocumentArchiveCommands,
  i18nLocale: I18nLocale
): (FloatingActionBarAction | DisabledFloatingActionBarAction)[] => {
  const { moveTo, deleteItem } = toBulkActionItems(
    selectedFolderIds,
    selectedAttachmentIds,
    commands.globalDocumentArchiveCommands.documentArchiveTable
      .attachmentMenuItem,
    i18nLocale
  );

  const moveToOrgAction = toMoveToOrgFloatingAction(
    workspaceId,
    selectedFolderIds,
    selectedAttachmentIds,
    namingConflicts,
    commands,
    i18nLocale
  );

  return [moveTo, moveToOrgAction, deleteItem];
};

const toNamingConflictPopover = (
  namingConflicts: NamedEntity[],
  i18nLocale: I18nLocale
) => {
  if (namingConflicts.length === 0) {
    return null;
  }

  return {
    items: namingConflicts.map(conflict => ({
      key: conflict._id,
      name: conflict.name,
      onClick: () => dispatchAction(scrollAndHighlightRow(conflict._id)),
    })),
    message: t(
      i18nLocale,
      'workspaceManager:documentArchive.namingConflictMessage',
      'The following items have naming conflicts with items in the global organization documents and cannot be moved until the conflicts are resolved:'
    ),
  };
};

const toExternalDropZoneProps = (
  workspaceId: ArdoqId,
  commands: WorkspaceDocumentArchiveCommands
): ExternalDropZonesProps<GroupedEntity> => ({
  dropZoneIds: [MOVE_TO_GLOBAL_DROP_ZONE_SELECTOR],
  onExternalDrop: (dropZoneId, rows) => {
    if (dropZoneId === MOVE_TO_GLOBAL_DROP_ZONE_SELECTOR) {
      commands.transformToGlobal(
        workspaceId,
        rows
          .filter(row => row.data._group.isFolder === false)
          .map(row => row.id),
        rows.filter(row => row.data._group.isFolder === true).map(row => row.id)
      );
    }
  },
  onDragEnterDropZone: id => dispatchAction(dragEnteredDropZone(id)),
  onDragLeaveDropZone: id => dispatchAction(dragExitedDropZone(id)),
  onDragEnd: () => dispatchAction(dragCompleted()),
  onDragStart: () => dispatchAction(dragInitiated()),
});
